import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Card,
  CardContent,
  Container,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Person as PersonIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Gavel as GavelIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { userService } from '../services/userService';
import { householdService } from '../services/householdService';
import authService from '../services/authService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSentryTracking } from './SentryComponentWrapper';
import { 
  trackFormSubmission, 
  trackUIInteraction, 
  trackAPICall,
  addBreadcrumb, 
  logger, 
  setContext,
  setTag,
  trackError 
} from '../utils/sentryUtils';

// Configuration: Set to true to disable new household signups (season has started)
const NEW_HOUSEHOLD_SIGNUPS_DISABLED = true;

const CaregiverSignUpForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Initialize Sentry tracking for this component
  useSentryTracking('CaregiverSignUpForm', { includeUpdates: true, includeRender: true });
  
  const [formData, setFormData] = useState({
    firstName: '',
    preferredName: '',
    middleName: '',
    noMiddleName: false,
    lastName: '',
    email: '',
    phoneNumber: '',
    primaryAddressStreet: '',
    primaryAddressCity: '',
    primaryAddressZip: '',
    gender: '',
    district: '',
    school: '',
    graduationYear: '',
    tshirtSize: '',
    optInCommunications: false,
    isActive: true,
    roleIds: [],
    // Household options
    householdMode: '', // Empty initially - user must select
    // For creating new household
    householdName: '',
    // For joining existing household
    householdCode: '',
    // Privacy policy consent
    privacyPolicyConsent: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [searchingHousehold, setSearchingHousehold] = useState(false);
  const [householdSelectionMade, setHouseholdSelectionMade] = useState(false);
  const [householdCodePrefilled, setHouseholdCodePrefilled] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);

  // Handle URL parameters for pre-filling household code
  useEffect(() => {
    const householdCode = searchParams.get('householdCode');
    const mode = searchParams.get('mode');
    
    // Set Sentry context for form initialization
    setContext("signup_form", {
      formType: "caregiver",
      householdCode: householdCode,
      mode: mode,
      urlParams: Object.fromEntries(searchParams.entries())
    });
    
    setTag("signup.form_type", "caregiver");
    setTag("signup.has_household_code", !!householdCode);
    setTag("signup.mode", mode || "manual");
    
    addBreadcrumb("Caregiver signup form opened", "ui.form", {
      formType: "caregiver",
      householdCode: householdCode,
      mode: mode
    });
    
    logger.info("Caregiver signup form initialized", {
      formType: "caregiver",
      householdCode: householdCode,
      mode: mode,
      urlParams: Object.fromEntries(searchParams.entries())
    });
    
    if (householdCode) {
      setFormData(prev => {
        const newData = {
          ...prev,
          householdCode: householdCode.toUpperCase(),
          // If household code is provided, force mode to 'join' regardless of mode parameter
          householdMode: 'join'
        };
        return newData;
      });
      
      // Set household selection made to show the household fields
      setHouseholdSelectionMade(true);
      // Mark that household code was pre-filled to disable create option
      setHouseholdCodePrefilled(true);
      
      // Look up the household code since it's provided
      const lookupHousehold = async (code) => {
        if (!code || code.trim().length !== 4) {
          setSelectedHousehold(null);
          return;
        }

        try {
          setSearchingHousehold(true);
          const household = await householdService.getHouseholdByCode(code.trim().toUpperCase());
          setSelectedHousehold(household);
          if (errors.householdCode) {
            setErrors(prev => ({
              ...prev,
              householdCode: ''
            }));
          }
        } catch (err) {
          setSelectedHousehold(null);
          setErrors(prev => ({
            ...prev,
            householdCode: 'Invalid household code. Please check and try again.'
          }));
          console.error('Error looking up household code:', err);
        } finally {
          setSearchingHousehold(false);
        }
      };
      
      lookupHousehold(householdCode.toUpperCase());
    }
  }, [searchParams]);

  // Check if this is for adding additional caregiver to existing household
  const isAddingAdditionalCaregiver = () => {
    return searchParams.get('householdCode') && searchParams.get('mode') === 'join';
  };

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format based on length
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length <= 3) return `(${phoneNumber}`;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handlePhoneNumberChange = (event) => {
    const value = event.target.value;
    const formattedValue = formatPhoneNumber(value);
    
    setFormData(prev => ({
      ...prev,
      phoneNumber: formattedValue
    }));
    
    // Clear error when user starts typing
    if (errors.phoneNumber) {
      setErrors(prev => ({
        ...prev,
        phoneNumber: ''
      }));
    }
  };

  // Zip code validation function
  const handleZipCodeChange = (event) => {
    const value = event.target.value;
    // Only allow numbers
    const numericValue = value.replace(/\D/g, '');
    
    setFormData(prev => ({
      ...prev,
      primaryAddressZip: numericValue
    }));
  };

  const handleHouseholdCodeLookup = async (code) => {
    if (!code || code.trim().length !== 4) {
      setSelectedHousehold(null);
      return;
    }

    return trackAPICall(
      "GET",
      `/api/households/code/${code.trim().toUpperCase()}`,
      async () => {
        setSearchingHousehold(true);
        
        addBreadcrumb("Looking up household code", "api", {
          code: code.trim().toUpperCase(),
          formType: "caregiver"
        });
        
        try {
          const household = await householdService.getHouseholdByCode(code.trim().toUpperCase());
          setSelectedHousehold(household);
          
          if (errors.householdCode) {
            setErrors(prev => ({
              ...prev,
              householdCode: ''
            }));
          }
          
          logger.info("Household code lookup successful", {
            code: code.trim().toUpperCase(),
            householdId: household?.id,
            formType: "caregiver"
          });
          
          return household;
        } catch (err) {
          setSelectedHousehold(null);
          setErrors(prev => ({
            ...prev,
            householdCode: 'Invalid household code. Please check and try again.'
          }));
          
          trackError(err, {
            component: "CaregiverSignUpForm",
            action: "household_code_lookup",
            code: code.trim().toUpperCase()
          });
          
          logger.error("Household code lookup failed", {
            code: code.trim().toUpperCase(),
            error: err.message,
            formType: "caregiver"
          });
          
          throw err;
        } finally {
          setSearchingHousehold(false);
        }
      },
      {
        code: code.trim().toUpperCase(),
        formType: "caregiver"
      }
    );
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    // Clear email availability status when email changes
    if (field === 'email') {
      setEmailAvailable(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    // Middle name validation: either middle name must be filled OR "I don't have a middle name" must be checked
    if (!formData.middleName.trim() && !formData.noMiddleName) {
      newErrors.middleName = 'Please enter your middle name or check "I don\'t have a middle name"';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    } else if (emailAvailable === false) {
      newErrors.email = 'This email address is already in use. Please choose a different one.';
    }
    
    // Phone number is required for caregivers
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required for caregivers';
    }
    
    // Gender identity is required for caregivers
    if (!formData.gender) {
      newErrors.gender = 'Gender identity is required';
    }
    
    // Privacy policy consent is required
    if (!formData.privacyPolicyConsent) {
      newErrors.privacyPolicyConsent = 'You must read and accept the Privacy Policy to continue';
    }
    
    // Check if household mode has been selected
    if (!formData.householdMode) {
      newErrors.householdMode = 'Please select a household option';
    }
    
    // Prevent creating new household if disabled
    if (formData.householdMode === 'create' && NEW_HOUSEHOLD_SIGNUPS_DISABLED) {
      newErrors.householdMode = 'New household signups are currently disabled. Please join an existing household instead.';
    }
    
    // Validate based on household mode
    if (formData.householdMode === 'create') {
      if (!formData.householdName.trim()) {
        newErrors.householdName = 'Household name is required';
      }
      if (!formData.primaryAddressStreet.trim()) {
        newErrors.primaryAddressStreet = 'Address is required';
      }
      if (!formData.primaryAddressCity.trim()) {
        newErrors.primaryAddressCity = 'City is required';
      }
      if (!formData.primaryAddressZip.trim()) {
        newErrors.primaryAddressZip = 'ZIP code is required';
      }
    } else if (formData.householdMode === 'join') {
      if (!formData.householdCode.trim()) {
        newErrors.householdCode = 'Household code is required';
      } else if (formData.householdCode.trim().length !== 4) {
        newErrors.householdCode = 'Household code must be 4 characters';
      } else if (!selectedHousehold) {
        newErrors.householdCode = 'Please enter a valid household code';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Track form submission with Sentry
    return trackFormSubmission(
      "CaregiverSignUpForm",
      async () => {
        if (!validateForm()) {
          throw new Error("Form validation failed");
        }
        
        setLoading(true);
        setError('');
        setSuccess(''); // Clear previous success messages
        
        addBreadcrumb("Caregiver signup form submitted", "ui.form", {
          formType: "caregiver",
          householdMode: formData.householdMode,
          hasHouseholdCode: !!formData.householdCode
        });
        
        logger.info("Caregiver signup form submitted", {
          formType: "caregiver",
          householdMode: formData.householdMode,
          hasHouseholdCode: !!formData.householdCode,
          formFields: Object.keys(formData)
        });
        
        try {
        let household;
        
        // Prevent creating new household if disabled
        if (formData.householdMode === 'create' && NEW_HOUSEHOLD_SIGNUPS_DISABLED) {
          throw new Error('New household signups are currently disabled. Please join an existing household instead.');
        }
        
        if (formData.householdMode === 'create') {
          // Don't create household separately - send household info with user creation
          // The backend will handle household creation during user creation
        } else {
          // Use selected household from code lookup
          household = selectedHousehold;
        }
        
        // Create the user data - the backend will automatically assign the caregiver role
        const userData = {
          ...formData,
          // Filter out empty enum values
          gender: formData.gender || null,
          tshirtSize: formData.tshirtSize || null,
          // Don't specify roleIds - backend will automatically assign caregiver role
          // If joining existing household, include householdIds
          // If creating new household, include household info in the user data
          ...(formData.householdMode === 'join' && household ? { householdIds: [household.id] } : {}),
          // Include household creation info if creating new household
          ...(formData.householdMode === 'create' ? {
            householdName: formData.householdName,
            // Address fields are already included in formData
          } : {}),
        };
        
        let signupResponse;
        
        // Use different endpoints based on whether this is adding to existing household
        if (isAddingAdditionalCaregiver()) {
          // Use the endpoint for adding additional caregivers (no auto-login)
          signupResponse = await userService.addCaregiverToHousehold(userData);
        } else {
          // Use the regular caregiver signup endpoint (with auto-login)
          signupResponse = await userService.caregiverSignup(userData);
        }
        
        if (!signupResponse.success) {
          throw new Error(signupResponse.message || 'Failed to register caregiver');
        }
        
        // Handle response based on endpoint used
        if (isAddingAdditionalCaregiver()) {
          // For additional caregivers, just show success and redirect back to household dashboard
          // No need to store tokens or user data
          setSuccess('Additional caregiver added successfully to household!');
          
          // Reset form data
          setFormData({
            firstName: '',
            preferredName: '',
            middleName: '',
            noMiddleName: false,
            lastName: '',
            email: '',
            phoneNumber: '',
            primaryAddressStreet: '',
            primaryAddressCity: '',
            primaryAddressZip: '',
            additionalAddressStreet: '',
            additionalAddressCity: '',
            additionalAddressZip: '',
            gender: '',
            district: '',
            school: '',
            graduationYear: '',
            tshirtSize: '',
            optInCommunications: false,
            isActive: true,
            roleIds: [],
            householdMode: '',
            householdName: '',
            householdCode: '',
            privacyPolicyConsent: false,
          });
          setHouseholdSelectionMade(false);
          setSelectedHousehold(null);
          setHouseholdCodePrefilled(false);
          
          // Redirect back to household dashboard after a short delay
          setTimeout(() => {
            navigate('/household/dashboard');
          }, 2000);
          
        } else {
          // For new caregivers creating households, proceed with normal flow
          // Store the JWT tokens and user data
          authService.storeTokens(signupResponse.accessToken, signupResponse.refreshToken);
          authService.storeUser(signupResponse.user);
          
          // Also store household information in session storage for compatibility
          sessionStorage.setItem('householdAuthenticated', 'true');
          // Get household ID from user response
          const createdUser = signupResponse.user;
          const householdId = createdUser.householdIds && createdUser.householdIds.length > 0 ? createdUser.householdIds[0] : null;
          if (householdId) {
            sessionStorage.setItem('householdId', householdId.toString());
          }
          sessionStorage.setItem('userId', createdUser.id.toString());
          sessionStorage.setItem('userEmail', formData.email);
          const displayName = formData.preferredName || formData.firstName;
          const lastNameInitial = formData.lastName ? formData.lastName.substring(0, 1) : '';
          const graduationYear = formData.graduationYear || '';
          sessionStorage.setItem('userName', `${displayName} ${lastNameInitial}. ${graduationYear}`);
          
          // Reset form data
          setFormData({
            firstName: '',
            preferredName: '',
            middleName: '',
            noMiddleName: false,
            lastName: '',
            email: '',
            phoneNumber: '',
            primaryAddressStreet: '',
            primaryAddressCity: '',
            primaryAddressZip: '',
            additionalAddressStreet: '',
            additionalAddressCity: '',
            additionalAddressZip: '',
            gender: '',
            district: '',
            school: '',
            graduationYear: '',
            tshirtSize: '',
            optInCommunications: false,
            isActive: true,
            roleIds: [],
            householdMode: '',
            householdName: '',
            householdCode: '',
            privacyPolicyConsent: false,
          });
          setHouseholdSelectionMade(false);
          setSelectedHousehold(null);
          setHouseholdCodePrefilled(false);
          
          // Redirect to household dashboard
          navigate('/household/dashboard');
        }

      } catch (err) {
        // Track error with Sentry
        trackError(err, {
          component: "CaregiverSignUpForm",
          action: "form_submission",
          householdMode: formData.householdMode,
          hasHouseholdCode: !!formData.householdCode
        });
        
        logger.error("Caregiver signup failed", {
          component: "CaregiverSignUpForm",
          error: err.message,
          householdMode: formData.householdMode,
          hasHouseholdCode: !!formData.householdCode
        });
        
        // Provide more specific error messages based on response status and data
        let errorMessage = 'Registration failed';
        
        if (err.response) {
          const status = err.response.status;
          const data = err.response.data;
          
          if (status === 409) {
            // Conflict - duplicate email or other constraint violation
            if (data && data.message) {
              errorMessage = data.message;
            } else {
              errorMessage = 'An account with this email address already exists. Please use a different email or try logging in instead.';
            }
          } else if (status === 400) {
            // Bad request - validation errors
            if (data && data.message) {
              errorMessage = `Registration failed: ${data.message}`;
            } else if (data && data.errors) {
              // Handle validation errors object
              const validationErrors = Object.values(data.errors).join(', ');
              errorMessage = `Please fix the following errors: ${validationErrors}`;
            } else {
              errorMessage = 'Registration failed: Please check your information and try again.';
            }
          } else if (status === 500) {
            // Server error
            if (data && data.message) {
              errorMessage = data.message;
            } else {
              errorMessage = 'Registration failed due to a server error. Please try again later.';
            }
          } else {
            // Other status codes
            if (data && data.message) {
              errorMessage = `Registration failed: ${data.message}`;
            } else {
              errorMessage = `Registration failed with status ${status}. Please try again.`;
            }
          }
        } else if (err.message) {
          // Network or other errors
          if (err.message.includes('Failed to register caregiver')) {
            errorMessage = err.message;
          } else if (err.message.includes('Network Error') || err.message.includes('timeout')) {
            errorMessage = 'Registration failed: Unable to connect to the server. Please check your internet connection and try again.';
          } else {
            errorMessage = `Registration failed: ${err.message}`;
          }
        } else {
          errorMessage = 'Registration failed. Please check your information and try again.';
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    {
      formFields: Object.keys(formData),
      householdMode: formData.householdMode,
      hasHouseholdCode: !!formData.householdCode,
      hasValidationErrors: Object.keys(errors).length > 0
    }
    )};

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ mb: 2 }}
        >
          Back to Registration Types
        </Button>
        <Typography 
          variant={isMobile ? "h4" : "h3"} 
          component="h1" 
          gutterBottom 
          color="primary"
          sx={{ 
            fontWeight: 'bold',
            mb: 1
          }}
        >
          {isAddingAdditionalCaregiver() ? 'Add Additional Caregiver' : 'Caregiver Registration'}
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ 
            mb: 3,
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          {isAddingAdditionalCaregiver() 
            ? 'Add a new parent, guardian, or caregiver to your existing household'
            : 'Register as a parent, guardian, or caregiver and create your household'
          }
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          {error.includes('email address already exists') && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                💡 Tip: If you already have an account, you can{' '}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => navigate('/login')}
                  sx={{ 
                    p: 0, 
                    minWidth: 'auto',
                    textDecoration: 'underline',
                    color: 'inherit',
                    '&:hover': {
                      textDecoration: 'none'
                    }
                  }}
                >
                  log in here
                </Button>
                {' '}or use a different email address to create a new account.
              </Typography>
            </Box>
          )}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Main Form */}
      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box component="form" onSubmit={handleSubmit}>
          
          {/* Personal Information Section */}
          <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: 'primary.main', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ mr: 1.5, fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Caregiver Information
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Tell us about yourself
            </Typography>
          </Box>
          
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Grid container spacing={3}>
              {/* Name Fields */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name (Legal)"
                  value={formData.firstName}
                  onChange={handleChange('firstName')}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  required
                  size="medium"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name (Legal)"
                  value={formData.lastName}
                  onChange={handleChange('lastName')}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  required
                  size="medium"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Preferred Name"
                  value={formData.preferredName}
                  onChange={handleChange('preferredName')}
                  helperText="Name you go by (e.g., Matt for Matthew)"
                  size="medium"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <TextField
                    fullWidth
                    label="Middle Name"
                    value={formData.middleName}
                    onChange={handleChange('middleName')}
                    disabled={formData.noMiddleName}
                    error={!!errors.middleName}
                    helperText={errors.middleName}
                    size="medium"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.noMiddleName}
                        onChange={handleChange('noMiddleName')}
                        size="small"
                      />
                    }
                    label="I don't have a middle name"
                    sx={{ ml: 0 }}
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  onBlur={async () => {
                    if (formData.email.trim() && !emailChecking) {
                      setEmailChecking(true);
                      setEmailAvailable(null);
                      try {
                        const result = await userService.checkEmailAvailability(formData.email);
                        if (result.available) {
                          setEmailAvailable(true);
                          // Clear any existing email error
                          if (errors.email && errors.email.includes('already in use')) {
                            setErrors(prev => ({
                              ...prev,
                              email: ''
                            }));
                          }
                        } else {
                          setEmailAvailable(false);
                          setErrors(prev => ({
                            ...prev,
                            email: 'This email address is already in use. Please choose a different one.'
                          }));
                        }
                      } catch (err) {
                        setEmailAvailable(false);
                        // Don't set error for network issues, just show as unavailable
                      } finally {
                        setEmailChecking(false);
                      }
                    }
                  }}
                  error={!!errors.email}
                  helperText={errors.email || (emailChecking ? 'Checking availability...' : emailAvailable === true ? 'Email available!' : emailAvailable === false ? 'Email not available' : "Use a unique email address that you haven't used before")}
                  required
                  size="medium"
                  inputProps={{
                    placeholder: "your.email@example.com"
                  }}
                  InputProps={{
                    endAdornment: emailChecking ? (
                      <CircularProgress size={20} />
                    ) : emailAvailable === true ? (
                      <CheckCircleIcon color="success" />
                    ) : emailAvailable === false ? (
                      <CancelIcon color="error" />
                    ) : null,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: emailAvailable === true ? 'success.main' : emailAvailable === false ? 'error.main' : 'primary.main',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={handlePhoneNumberChange}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber || "e.g., (555) 123-4567"}
                  required
                  size="medium"
                  inputProps={{
                    maxLength: 14, // (XXX) XXX-XXXX format
                    placeholder: "(555) 123-4567"
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="medium" sx={{ minWidth: 160 }}>
                  <InputLabel>Gender Identity *</InputLabel>
                  <Select
                    value={formData.gender}
                    onChange={handleChange('gender')}
                    label="Gender Identity *"
                    error={!!errors.gender}
                  >
                    <MenuItem value="MALE">Male</MenuItem>
                    <MenuItem value="FEMALE">Female</MenuItem>
                    <MenuItem value="NON_BINARY">Non-Binary</MenuItem>
                    <MenuItem value="NO_ANSWER">Prefer not to answer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>

          <Divider />

          {/* Household Information Section */}
          <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <HomeIcon sx={{ mr: 1.5, fontSize: 28, color: 'primary.main' }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Household Information
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Choose how you want to handle your household
            </Typography>
          </Box>
          
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            {/* Household Mode Selection */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Household Option
              </Typography>
              
              {/* Show disabled message if new household signups are disabled */}
              {NEW_HOUSEHOLD_SIGNUPS_DISABLED && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    New household signups are currently disabled
                  </Typography>
                  <Typography variant="body2">
                    The season has started and we are no longer accepting new household registrations. 
                    You can still join an existing household using a household code provided by your team coach or administrator.
                  </Typography>
                </Alert>
              )}
              
              <ToggleButtonGroup
                value={formData.householdMode}
                exclusive
                disabled={householdCodePrefilled || NEW_HOUSEHOLD_SIGNUPS_DISABLED}
                onChange={(event, newMode) => {
                  if (newMode !== null && !householdCodePrefilled && !NEW_HOUSEHOLD_SIGNUPS_DISABLED) {
                    setFormData(prev => ({
                      ...prev,
                      householdMode: newMode,
                      householdCode: '',
                      // Set default household name to last name when creating new household
                      householdName: newMode === 'create' && prev.lastName ? `${prev.lastName} Family` : '',
                    }));
                    setSelectedHousehold(null);
                    setHouseholdSelectionMade(true);
                    // Clear related errors
                    setErrors(prev => ({
                      ...prev,
                      householdMode: '',
                      householdName: '',
                      primaryAddressStreet: '',
                      householdCode: '',
                    }));
                  }
                }}
                sx={{ mb: 3 }}
              >
                <ToggleButton 
                  value="create" 
                  disabled={NEW_HOUSEHOLD_SIGNUPS_DISABLED}
                  sx={{ 
                    px: 3, 
                    py: 1.5,
                    ...(NEW_HOUSEHOLD_SIGNUPS_DISABLED && {
                      opacity: 0.5,
                      cursor: 'not-allowed'
                    })
                  }}
                >
                  <AddIcon sx={{ mr: 1 }} />
                  Create New Household
                  {NEW_HOUSEHOLD_SIGNUPS_DISABLED && (
                    <CancelIcon sx={{ ml: 1, fontSize: 16 }} />
                  )}
                </ToggleButton>
                <ToggleButton value="join" sx={{ px: 3, py: 1.5 }}>
                  <SearchIcon sx={{ mr: 1 }} />
                  Join Existing Household
                </ToggleButton>
              </ToggleButtonGroup>
              {errors.householdMode && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {errors.householdMode}
                </Typography>
              )}
              {householdCodePrefilled && (
                <Typography variant="body2" color="info.main" sx={{ mt: 1, fontStyle: 'italic' }}>
                  Household code was provided in the invitation link. You can only join the existing household.
                </Typography>
              )}
            </Box>

            {/* Only show household fields after selection is made */}
            {householdSelectionMade && (
              <>
                {formData.householdMode === 'create' ? (
                  /* Create New Household Form */
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Household Name"
                        value={formData.householdName}
                        onChange={handleChange('householdName')}
                        error={!!errors.householdName}
                        helperText={errors.householdName || "e.g., Smith Family, Johnson Household"}
                        required
                        size="medium"
                      />
                    </Grid>
                    

                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Street Address"
                        value={formData.primaryAddressStreet}
                        onChange={handleChange('primaryAddressStreet')}
                        error={!!errors.primaryAddressStreet}
                        helperText={errors.primaryAddressStreet || "Primary household address"}
                        required
                        size="medium"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="City"
                        value={formData.primaryAddressCity}
                        onChange={handleChange('primaryAddressCity')}
                        error={!!errors.primaryAddressCity}
                        helperText={errors.primaryAddressCity}
                        required
                        size="medium"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="ZIP Code"
                        value={formData.primaryAddressZip}
                        onChange={handleZipCodeChange}
                        error={!!errors.primaryAddressZip}
                        helperText={errors.primaryAddressZip}
                        required
                        size="medium"
                        inputProps={{
                          maxLength: 10, // Allow for ZIP+4 format
                          placeholder: "12345"
                        }}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  /* Join Existing Household Form */
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Household Code"
                        value={formData.householdCode}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          setFormData(prev => ({
                            ...prev,
                            householdCode: value
                          }));
                          if (value.length === 4) {
                            handleHouseholdCodeLookup(value);
                          } else {
                            setSelectedHousehold(null);
                          }
                          if (errors.householdCode) {
                            setErrors(prev => ({
                              ...prev,
                              householdCode: ''
                            }));
                          }
                        }}
                        error={!!errors.householdCode}
                        helperText={errors.householdCode || "Enter the 4-character household code"}
                        required
                        size="medium"
                        inputProps={{
                          maxLength: 4,
                          style: { textTransform: 'uppercase' }
                        }}
                        InputProps={{
                          endAdornment: searchingHousehold ? <CircularProgress size={20} /> : null,
                        }}
                      />
                    </Grid>
                    
                    {selectedHousehold && (
                      <Grid item xs={12} md={6}>
                        <Box sx={{ 
                          p: 2, 
                          border: 1, 
                          borderColor: 'success.main', 
                          borderRadius: 1,
                          backgroundColor: 'success.light',
                          color: 'success.contrastText'
                        }}>
                          <Typography variant="body2" fontWeight="bold" gutterBottom>
                            Household Found:
                          </Typography>
                          <Typography variant="body2">
                            {selectedHousehold.name}
                          </Typography>

                        </Box>
                      </Grid>
                    )}
                  </Grid>
                )}
              </>
            )}
          </CardContent>

          <Divider />



          {/* Privacy Policy Consent Section */}
          <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: 'primary.main', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <GavelIcon sx={{ mr: 1.5, fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Legal Consent
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Please review and accept our privacy policy to continue with registration
            </Typography>
          </Box>
          
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.privacyPolicyConsent}
                        onChange={handleChange('privacyPolicyConsent')}
                        color="primary"
                        required
                      />
                    }
                    label={
                      <Typography variant="body1">
                        I have read and understand the{' '}
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => window.open('/privacy-policy', '_blank')}
                          sx={{ 
                            p: 0, 
                            minWidth: 'auto',
                            textDecoration: 'underline',
                            color: 'primary.main',
                            '&:hover': {
                              textDecoration: 'none'
                            }
                          }}
                        >
                          RoboJackets K12 Foundation's Privacy Policy
                        </Button>
                        , including how my (or my child's) information will be used to operate and promote the FTC League and sister programs.
                      </Typography>
                    }
                    sx={{ 
                      alignItems: 'flex-start',
                      '& .MuiFormControlLabel-label': {
                        mt: 0.5
                      }
                    }}
                  />
                </Box>
                {errors.privacyPolicyConsent && (
                  <Typography variant="body2" color="error" sx={{ mt: 1, ml: 4 }}>
                    {errors.privacyPolicyConsent}
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.optInCommunications}
                      onChange={handleChange('optInCommunications')}
                      color="primary"
                      required
                    />
                  }
                  label="I understand and agree that my contact information will be used to provide communications and newsletters regarding the RoboJackets K12 Foundation and sister programs."
                />
              </Grid>
            </Grid>
          </CardContent>

          {/* Submit Section */}
          <Box sx={{ 
            p: { xs: 3, md: 4 }, 
            backgroundColor: 'primary.main', 
            color: 'white',
            textAlign: 'center'
          }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading || (NEW_HOUSEHOLD_SIGNUPS_DISABLED && !formData.householdMode)}
              sx={{ 
                minWidth: 200,
                py: 1.5,
                px: 4,
                backgroundColor: 'white',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'grey.100',
                },
                ...(NEW_HOUSEHOLD_SIGNUPS_DISABLED && !formData.householdMode && {
                  opacity: 0.5,
                  cursor: 'not-allowed',
                  '&:hover': {
                    backgroundColor: 'white',
                  }
                })
              }}
            >
              {loading ? <CircularProgress size={24} color="primary" /> : (isAddingAdditionalCaregiver() ? 'Add Caregiver to Household' : 'Complete Registration')}
            </Button>
            
            {/* Show message when button is disabled due to no household selection */}
            {NEW_HOUSEHOLD_SIGNUPS_DISABLED && !formData.householdMode && (
              <Typography variant="body2" sx={{ mt: 2, opacity: 0.8, fontStyle: 'italic' }}>
                Please select "Join Existing Household" above to continue with registration
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
      
      {/* Helpful Information Section */}
      <Paper elevation={1} sx={{ mt: 3, p: 3, borderRadius: 2, backgroundColor: 'info.light' }}>
        <Typography variant="h6" gutterBottom color="info.contrastText">
          💡 Need Help?
        </Typography>
        <Typography variant="body2" color="info.contrastText" sx={{ mb: 2 }}>
          If you encounter any issues during registration:
        </Typography>
        <Box component="ul" sx={{ pl: 2, m: 0 }}>
          <Typography component="li" variant="body2" color="info.contrastText" sx={{ mb: 1 }}>
            <strong>Email already in use?</strong> Try logging in instead, or use a different email address
          </Typography>
          <Typography component="li" variant="body2" color="info.contrastText" sx={{ mb: 1 }}>
            <strong>Household code issues?</strong> Contact your team coach or administrator
          </Typography>
          <Typography component="li" variant="body2" color="info.contrastText" sx={{ mb: 1 }}>
            <strong>Technical problems?</strong> Try refreshing the page or contact support
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default CaregiverSignUpForm; 